// §31 Phase 2: multi-file drag-drop intake – thin client. Everything visible is
// server-rendered: this module only does what the browser alone can (read the
// dropped FileList, expand folders) and fires the uploads. It opens the intake by
// POSTing the file list to /intake (the SSE loop renders the prepare modal), then
// raw-fetch uploads each file to /uploads (204); the per-file "bereit" patch and
// the whole rail ride the long-lived SSE via ui.intake.* events – never this
// module's fetch responses. Kept as its own small bundle, like webauthn.ts.

type IntakeKind = 'invoice' | 'booklet';

const ACCEPT_RE = /\.(jpe?g|png|webp|heic|heif|pdf)$/i;
const MAX_FILES = 100;
const UPLOAD_CONCURRENCY = 3;

// intakeStart is the drop-handler entry point. It must read the DataTransfer
// synchronously (the item list is only valid during the event), then go async.
// tabId is the SSE tab the modal patches into, passed straight from the $tabId
// signal by the drop expression (see pages/helpers.go).
function intakeStart(carID: string, kind: IntakeKind, dt: DataTransfer, tabId: string): void {
  const entries: FileSystemEntry[] = [];
  const looseFiles: File[] = [];
  if (dt.items && dt.items.length) {
    for (const item of Array.from(dt.items)) {
      const entry = (item as unknown as { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.();
      if (entry) {
        entries.push(entry);
      } else if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) looseFiles.push(f);
      }
    }
  } else if (dt.files) {
    looseFiles.push(...Array.from(dt.files));
  }
  if (!tabId) return; // no SSE tab to render into
  void runIntake(carID, kind, entries, looseFiles, tabId);
}

async function runIntake(carID: string, kind: IntakeKind, entries: FileSystemEntry[], looseFiles: File[], tabId: string): Promise<void> {
  let files: File[] = [...looseFiles];
  for (const e of entries) files.push(...(await walkEntry(e)));
  files = dedupe(files.filter((f) => ACCEPT_RE.test(f.name)));

  const t = intakeText();
  if (files.length === 0) {
    if (t.empty) window.__notify(t.empty, 'warn');
    return;
  }
  if (files.length > MAX_FILES) {
    if (t.tooMany) window.__notify(t.tooMany.replace('{n}', String(MAX_FILES)), 'warn');
    files = files.slice(0, MAX_FILES);
  }

  const batchId = (crypto.randomUUID?.() ?? String(Date.now() + Math.random()));

  // Declare the batch: the server inserts one placeholder staging row per file
  // (slot = rail position, mime type for the pending icon) and renders the modal
  // from those rows. The manifest is DB rows, not render payload.
  const fileList = files.map((f, slot) => ({ slot, name: f.name, type: f.type }));
  const opened = await fetch(`/api/cars/${carID}/intake`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tabId, batchId, kind, files: fileList }),
  });
  if (!opened.ok) return;

  // Eager-upload each file's bytes into its placeholder; the server publishes a
  // ui.intake.card patch as each lands.
  uploadFrom(carID, tabId, batchId, files, 0);
}

// addMore appends more files to an already-open batch (the §87 in-modal drop-more
// tile). It declares the new files on the SAME batch – the server assigns the next
// free slots and echoes {startSlot, accepted} – then uploads their bytes to those
// slots. No folder walk here: the tile takes a flat FileList (dropped or picked).
async function addMore(carID: string, batchId: string, raw: File[], tabId: string): Promise<void> {
  const files = dedupe(raw.filter((f) => ACCEPT_RE.test(f.name)));
  const t = intakeText();
  if (files.length === 0) {
    if (t.empty) window.__notify(t.empty, 'warn');
    return;
  }
  const manifest = files.map((f) => ({ name: f.name, type: f.type }));
  let res: Response;
  try {
    res = await fetch(`/api/cars/${carID}/intake/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tabId, batchId, files: manifest }),
    });
  } catch {
    return;
  }
  if (!res.ok) return;
  let startSlot = 0;
  let accepted = files.length;
  try {
    const j = (await res.json()) as { startSlot?: number; accepted?: number };
    startSlot = j.startSlot ?? 0;
    accepted = j.accepted ?? files.length;
  } catch {
    /* no JSON echo – fall back to uploading all from slot 0 is wrong, so bail */
    return;
  }
  uploadFrom(carID, tabId, batchId, files.slice(0, accepted), startSlot);
}

// ─── Folder expansion ─────────────────────────────────────────────────────────

async function walkEntry(entry: FileSystemEntry): Promise<File[]> {
  const anyEntry = entry as unknown as {
    isFile: boolean;
    isDirectory: boolean;
    file?: (ok: (f: File) => void, err: () => void) => void;
    createReader?: () => { readEntries: (ok: (e: FileSystemEntry[]) => void, err: () => void) => void };
  };
  if (anyEntry.isFile && anyEntry.file) {
    return new Promise<File[]>((res) => anyEntry.file!((f) => res([f]), () => res([])));
  }
  if (anyEntry.isDirectory && anyEntry.createReader) {
    const reader = anyEntry.createReader();
    const kids = await readAllEntries(reader);
    const out: File[] = [];
    for (const k of kids) out.push(...(await walkEntry(k)));
    return out;
  }
  return [];
}

function readAllEntries(reader: { readEntries: (ok: (e: FileSystemEntry[]) => void, err: () => void) => void }): Promise<FileSystemEntry[]> {
  return new Promise((resolve) => {
    const all: FileSystemEntry[] = [];
    const step = () =>
      reader.readEntries((batch) => {
        if (!batch.length) return resolve(all);
        all.push(...batch);
        step(); // readEntries yields in chunks; keep going until empty
      }, () => resolve(all));
    step();
  });
}

function dedupe(files: File[]): File[] {
  const seen = new Set<string>();
  return files.filter((f) => {
    const key = `${f.name}:${f.size}:${f.lastModified}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ─── Uploads (raw fetch; progress patches ride the long-lived SSE) ────────────

function uploadFrom(carID: string, tabId: string, batchId: string, files: File[], startSlot: number): void {
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < files.length) {
      const i = next++;
      const slot = startSlot + i; // absolute rail slot the server declared for this file
      const body = new FormData();
      body.append('file', files[i]);
      // §64: tab/batch/slot ride the query string, not the body, so the server can
      // scope upload-progress events before it parses the multipart body.
      const q = `tab=${encodeURIComponent(tabId)}&batch=${encodeURIComponent(batchId)}&slot=${slot}`;
      try {
        await fetch(`/api/cars/${carID}/uploads?${q}`, { method: 'POST', body });
      } catch {
        /* a failed upload just leaves its card pending; the server never patches it */
      }
    }
  };
  void Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, worker));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// intakeText reads the two client-only alert strings the server rendered (folder
// with nothing supported / too many files). Everything else is in the templ modal.
function intakeText(): { empty: string; tooMany: string } {
  const el = document.getElementById('intake-i18n');
  return {
    empty: el?.getAttribute('data-empty') || '',
    tooMany: el?.getAttribute('data-too-many') || '',
  };
}

// ─── §31 Phase 3 editor: zoom / crop ──────────────────────────────────────────
//
// Rotation is a Datastar-driven CSS transform on the <img> ($editAngle). Dragging on
// the stage ALWAYS draws the crop rectangle (no mode toggle); zoom is the ± buttons /
// wheel / pinch (a transform on the #intake-edit-zoom container). Crop is normalized
// against the image's live bounding box – which composes rotate + zoom – so it maps
// exactly onto the server's post-rotation box (imaging.Rotate output). The drawn rect
// is written to a hidden input bound to $editCrop, so "Anwenden"'s @post carries it.
// Nothing here rasterizes the image.

interface CropRect { x: number; y: number; w: number; h: number; }
const ZMIN = 1, ZMAX = 6;
const ed = {
  zoom: 1, panX: 0, panY: 0,
  crop: null as CropRect | null,
  raf: 0,
  drag: null as null | { sx: number; sy: number }, // crop-draw start (normalized image coords)
  pointers: new Map<number, { x: number; y: number }>(),
  pinch: 0,
};

// The editor's elements are singletons (one modal at a time), so resolve them once
// when a card is selected – the modal + editor are freshly in the DOM then – instead
// of re-querying on every pointer move / animation frame. (data-ref would expose
// these as signals for Datastar *expressions*, but this controller is imperative TS
// and can't read Datastar's signal store, so a plain cache is the right tool.)
const E = {
  editor: null as HTMLElement | null,
  stage: null as HTMLElement | null,
  zoom: null as HTMLElement | null,
  img: null as HTMLImageElement | null,
  cropEl: null as HTMLElement | null,
  input: null as HTMLInputElement | null,
  zoomLabel: null as HTMLElement | null,
};

function cacheEls(): void {
  E.editor = document.getElementById('intake-editor');
  E.stage = document.getElementById('intake-edit-stage');
  E.zoom = document.getElementById('intake-edit-zoom');
  E.img = document.getElementById('intake-edit-img') as HTMLImageElement | null;
  E.cropEl = document.getElementById('intake-crop');
  E.input = document.getElementById('edit-crop-input') as HTMLInputElement | null;
  E.zoomLabel = document.getElementById('intake-edit-zoomlabel');
}

// intakeEditLoad (card click): (re)resolve the editor elements, load the crisp
// preview + any existing crop, reset the view, and start the overlay render loop.
// The card carries its slot + stored crop; the stage carries car + batch ids.
function intakeEditLoad(card: HTMLElement): void {
  cacheEls();
  const slot = card.dataset.slot ?? '';
  const thumb = card.querySelector<HTMLImageElement>('.intake-card__thumb img')?.src;
  if (E.img && thumb) E.img.src = thumb; // instant placeholder
  if (E.img && E.stage) {
    const url = `/api/cars/${E.stage.dataset.car}/uploads/preview?batch=${E.stage.dataset.batch}&slot=${slot}`;
    const hi = new Image();
    hi.onload = () => { if (E.img) E.img.src = url; }; // swap to crisp when decoded
    hi.src = url;
  }
  ed.zoom = 1; ed.panX = 0; ed.panY = 0;
  ed.crop = parseCropAttr(card.dataset.crop);
  if (E.stage) E.stage.style.cursor = 'crosshair'; // dragging always draws the crop
  applyView(); writeCrop();
  startLoop();
}

function parseCropAttr(s?: string): CropRect | null {
  if (!s) return null;
  try { const c = JSON.parse(s) as CropRect; if (c && c.w > 0 && c.h > 0) return c; } catch { /* ignore */ }
  return null;
}

function intakeEditReset(): void {
  ed.zoom = 1; ed.panX = 0; ed.panY = 0; ed.crop = null;
  applyView(); writeCrop();
}

// intakeZoom: the ± buttons zoom about the stage center; the wheel/pinch zoom about
// the cursor/pinch midpoint (see zoomAt callers).
function intakeZoom(dir: number): void { zoomAt(0, 0, dir > 0 ? 1.25 : 1 / 1.25); }

// zoomAt scales about a stage-center-relative point (cx,cy in px), keeping that point
// fixed – matches the container transform `translate(pan) scale(zoom)` (origin center).
function zoomAt(cx: number, cy: number, factor: number): void {
  const prev = ed.zoom;
  ed.zoom = Math.min(ZMAX, Math.max(ZMIN, prev * factor));
  const k = ed.zoom / prev;
  ed.panX = cx - (cx - ed.panX) * k;
  ed.panY = cy - (cy - ed.panY) * k;
  if (ed.zoom === 1) { ed.panX = 0; ed.panY = 0; }
  applyView();
}

function applyView(): void {
  if (E.zoom) E.zoom.style.transform = `translate(${ed.panX}px, ${ed.panY}px) scale(${ed.zoom})`;
  if (E.zoomLabel) E.zoomLabel.textContent = Math.round(ed.zoom * 100) + '%';
}

// writeCrop pushes the current rect to the hidden input → $editCrop (data-bind).
function writeCrop(): void {
  if (!E.input) return;
  E.input.value = ed.crop ? JSON.stringify(ed.crop) : '';
  E.input.dispatchEvent(new Event('input', { bubbles: true }));
}

// startLoop positions the crop overlay from the image's live bounding box every
// frame (so it tracks rotate/zoom/pan), stopping itself once the editor is hidden.
function startLoop(): void {
  if (ed.raf) return;
  const tick = () => {
    if (!E.editor || !E.stage || getComputedStyle(E.editor).display === 'none') { ed.raf = 0; return; }
    if (E.img && E.cropEl && ed.crop) {
      const ir = E.img.getBoundingClientRect();
      const sr = E.stage.getBoundingClientRect();
      E.cropEl.hidden = false;
      E.cropEl.style.left = `${ir.left - sr.left + ed.crop.x * ir.width}px`;
      E.cropEl.style.top = `${ir.top - sr.top + ed.crop.y * ir.height}px`;
      E.cropEl.style.width = `${ed.crop.w * ir.width}px`;
      E.cropEl.style.height = `${ed.crop.h * ir.height}px`;
    } else if (E.cropEl) {
      E.cropEl.hidden = true;
    }
    ed.raf = requestAnimationFrame(tick);
  };
  ed.raf = requestAnimationFrame(tick);
}

// ── pointer / wheel input (delegated on document so it survives modal re-patching) ──

// overStage: is the event inside the (cached, currently-open) editor stage?
function overStage(t: EventTarget | null): boolean {
  return !!(E.stage && t instanceof Node && E.stage.contains(t));
}

function cropPoint(img: HTMLImageElement, clientX: number, clientY: number): { x: number; y: number } {
  const ir = img.getBoundingClientRect();
  return {
    x: Math.min(1, Math.max(0, (clientX - ir.left) / ir.width)),
    y: Math.min(1, Math.max(0, (clientY - ir.top) / ir.height)),
  };
}

function normRect(x0: number, y0: number, x1: number, y1: number): CropRect {
  return { x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
}

document.addEventListener('pointerdown', (e) => {
  if (!overStage(e.target) || !E.img || !E.stage) return;
  ed.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (ed.pointers.size === 2) {
    const [a, b] = [...ed.pointers.values()];
    ed.pinch = Math.hypot(a.x - b.x, a.y - b.y);
    ed.drag = null;
    return;
  }
  try { E.stage.setPointerCapture?.(e.pointerId); } catch { /* non-active pointer */ }
  const p = cropPoint(E.img, e.clientX, e.clientY);
  ed.drag = { sx: p.x, sy: p.y };
  ed.crop = { x: p.x, y: p.y, w: 0, h: 0 };
  e.preventDefault();
});

document.addEventListener('pointermove', (e) => {
  if (!ed.pointers.has(e.pointerId) || !E.stage || !E.img) return;
  ed.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (ed.pointers.size === 2) {
    const [a, b] = [...ed.pointers.values()];
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    if (ed.pinch > 0) {
      const sr = E.stage.getBoundingClientRect();
      const mx = (a.x + b.x) / 2 - sr.left - sr.width / 2;
      const my = (a.y + b.y) / 2 - sr.top - sr.height / 2;
      zoomAt(mx, my, dist / ed.pinch);
    }
    ed.pinch = dist;
    return;
  }
  if (!ed.drag) return;
  const p = cropPoint(E.img, e.clientX, e.clientY);
  ed.crop = normRect(ed.drag.sx, ed.drag.sy, p.x, p.y);
});

function endPointer(e: PointerEvent): void {
  if (!ed.pointers.has(e.pointerId)) return;
  ed.pointers.delete(e.pointerId);
  if (ed.pointers.size < 2) ed.pinch = 0;
  if (ed.drag) {
    if (!ed.crop || ed.crop.w < 0.02 || ed.crop.h < 0.02) ed.crop = null; // a tap clears the crop
    writeCrop();
    ed.drag = null;
  }
}
document.addEventListener('pointerup', endPointer);
document.addEventListener('pointercancel', endPointer);

document.addEventListener('wheel', (e) => {
  if (!overStage(e.target) || !E.stage) return;
  e.preventDefault();
  const sr = E.stage.getBoundingClientRect();
  zoomAt(e.clientX - sr.left - sr.width / 2, e.clientY - sr.top - sr.height / 2, e.deltaY < 0 ? 1.1 : 1 / 1.1);
}, { passive: false });

// __intakeAddMore is the in-modal drop-more entry point (tile drop / picker change).
// It takes a flat FileList and appends it to the open batch.
function intakeAddMore(carID: string, batchId: string, files: FileList | File[], tabId: string): void {
  if (!tabId) return;
  void addMore(carID, batchId, Array.from(files), tabId);
}

declare global {
  interface Window {
    __intakeStart: typeof intakeStart;
    __intakeAddMore: typeof intakeAddMore;
    __intakeEditLoad: typeof intakeEditLoad;
    __intakeZoom: typeof intakeZoom;
    __intakeEditReset: typeof intakeEditReset;
  }
}
window.__intakeStart = intakeStart;
window.__intakeAddMore = intakeAddMore;
window.__intakeEditLoad = intakeEditLoad;
window.__intakeZoom = intakeZoom;
window.__intakeEditReset = intakeEditReset;

export {};
